import React, { useState } from 'react';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import {
  IncidentArea,
  useCreateIncidentArea,
  useDeleteIncidentArea,
  useIncidentAreas,
  useUpdateIncidentArea,
} from '../../hooks/incidentAreaHooks';
import { useActualIncidentId } from '../../hooks/commonHooks';
import { useForm } from 'react-hook-form';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useSearchParams } from 'react-router';
import ICSWidget from './ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';
import { NULL_VALUE } from 'constants/displayConstants';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';

const riskGainOptions = [
  { label: 'Low/Low', value: 'Low/Low' },
  { label: 'Low/Med', value: 'Low/Med' },
  { label: 'Low/High', value: 'Low/High' },
  { label: 'Med/Low', value: 'Med/Low' },
  { label: 'Med/Med', value: 'Med/Med' },
  { label: 'Med/High', value: 'Med/High' },
  { label: 'High/Low', value: 'High/Low' },
  { label: 'High/Med', value: 'High/Med' },
  { label: 'High/High', value: 'High/High' },
];
interface IncidentAreaForm {
  areaName: string;
  hazard: string;
  mitigations: string;
  riskGain: string | null;
}

const IncidentAreaModal: React.FC<{
  show: boolean;
  setShow: (show: boolean) => void;
  incidentId: string;
}> = ({ show, setShow, incidentId }) => {
  const mutation = useCreateIncidentArea();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IncidentAreaForm>({
    defaultValues: {
      areaName: '',
      hazard: '',
      mitigations: '',
      riskGain: null,
    },
  });

  const [riskGainValue, setRiskGainValue] = useState<string | null>(null);

  const onSubmit = (data: IncidentAreaForm) => {
    mutation.mutate({
      incidentId,
      ...data,
    });
    setShow(false);
    reset();
    setRiskGainValue(null);
  };

  return (
    <CustomModal
      show={show}
      heading="Create Incident Area"
      onClose={() => {
        setShow(false);
        reset();
        setRiskGainValue(null);
      }}
      onConfirm={handleSubmit(onSubmit)}
      saveButton="Add"
      buttonDisable={Object.keys(errors).length > 0}
      staticBackdrop={false}
    >
      <div className="mb-3">
        <label>Area Name *</label>
        <StylishNewInput
          {...register('areaName', { required: 'Area name is required' })}
        />
        {errors.areaName && (
          <span className="form-text form-error">
            {errors.areaName.message}
          </span>
        )}
      </div>

      <div className="mb-3">
        <label>Hazard/Risks *</label>
        <StylishNewInput
          {...register('hazard', { required: 'Hazards are required' })}
        />
        {errors.hazard && (
          <span className="form-text form-error">{errors.hazard.message}</span>
        )}
      </div>

      <div className="mb-3">
        <label>Mitigations *</label>
        <StylishNewInput
          {...register('mitigations', { required: 'Mitigations are required' })}
        />
        {errors.mitigations && (
          <span className="form-text form-error">
            {errors.mitigations.message}
          </span>
        )}
      </div>

      <div className="mb-3">
        <label>Risk/Gain</label>
        <StylishNewSelect
          value={
            riskGainValue
              ? { label: riskGainValue, value: riskGainValue }
              : null
          }
          isClearable
          placeholder="Select Risk/Gain..."
          options={riskGainOptions}
          onChange={(e: { value: string } | null) => {
            const val = e?.value ?? null;
            setRiskGainValue(val);
            setValue('riskGain', val);
          }}
        />
      </div>
    </CustomModal>
  );
};

type EditDraft = {
  areaName: string;
  hazard: string;
  mitigations: string;
  riskGain: string | null;
};

const IAPSafetyAnalysis: React.FC = () => {
  const [show, setShow] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [searchParams] = useSearchParams();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-iap-safety-analysis`;

  const incidentId = useActualIncidentId();
  const { data: incidentAreas } = useIncidentAreas(incidentId);
  const deleteMutation = useDeleteIncidentArea();
  const incidentMutation = useUpdateIncidentArea();
  const { exportAsForm } = useIcsFormPdfPreview();

  const handleExport = () => {
    void exportAsForm({ formname: 'ICS_215A_CG', data: {}, mapSketch: null });
  };

  const handleDelete = (area: IncidentArea) => {
    if (area.areaId) deleteMutation.mutate(area.areaId);
  };

  const startEditing = (area: IncidentArea) => {
    setEditingRowId(area.areaId ?? null);
    setEditDraft({
      areaName: area.areaName,
      hazard: area.hazard,
      mitigations: area.mitigations,
      riskGain: area.riskGain,
    });
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditDraft(null);
  };

  const saveEditing = (area: IncidentArea) => {
    if (!editDraft) return;
    incidentMutation.mutate(
      {
        ...area,
        ...editDraft,
        riskGain: editDraft.riskGain ?? null,
      },
      {
        onSuccess: () => {
          setEditingRowId(null);
          setEditDraft(null);
        },
      }
    );
  };
  return (
    <>
      <ConditionalCollapsibleCard
        sectionKey={sectionKey}
        className="planning-widget"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="shield"
            iconColor="primary"
            title={getIncidentCardTitle('iapSafetyAnalysis')}
            subtitle="Safety analysis and risk assessment for the Incident Action Plan"
          />
        }
        headerRight={
          <ICSWidget.HeaderRight
            icsRoles={['PSC', 'SOFR']}
            handleExport={handleExport}
            exportText="Preview ICS-215A"
          />
        }
      >
        <div className="d-flex justify-content-between mb-2">
          <h5 className="mt-0">
            Incident Areas: {incidentAreas?.length} area(s)
          </h5>
          {canEditCards && (
            <StylishNewButton
              className="button button--primary gap-1"
              onClick={() => setShow(true)}
            >
              <SharedIcon iconName="add" size="sm" /> Add Area
            </StylishNewButton>
          )}
        </div>
        <div>
          {incidentAreas && incidentAreas.length > 0 ? (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Area Name</th>
                    <th>Hazards/Risks</th>
                    <th>Mitigations</th>
                    <th>Risk/Gain</th>
                    {canEditCards && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {incidentAreas.map((area) => {
                    const editing = area.areaId === editingRowId && editDraft;
                    return (
                      <tr key={area.areaId}>
                        <td>
                          {editing ? (
                            <StylishNewInput
                              value={editDraft.areaName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditDraft({
                                  ...editDraft,
                                  areaName: e.target.value,
                                })
                              }
                            />
                          ) : (
                            area.areaName || NULL_VALUE
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <StylishNewInput
                              value={editDraft.hazard}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditDraft({
                                  ...editDraft,
                                  hazard: e.target.value,
                                })
                              }
                            />
                          ) : (
                            area.hazard || NULL_VALUE
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <StylishNewInput
                              value={editDraft.mitigations}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) =>
                                setEditDraft({
                                  ...editDraft,
                                  mitigations: e.target.value,
                                })
                              }
                            />
                          ) : (
                            area.mitigations || NULL_VALUE
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <StylishNewSelect
                              value={
                                editDraft.riskGain
                                  ? {
                                      label: editDraft.riskGain,
                                      value: editDraft.riskGain,
                                    }
                                  : null
                              }
                              isClearable
                              placeholder="Select Risk/Gain..."
                              options={riskGainOptions}
                              menuPortalTarget={document.body}
                              onChange={(e: { value: string } | null) =>
                                setEditDraft({
                                  ...editDraft,
                                  riskGain: e?.value ?? null,
                                })
                              }
                            />
                          ) : (
                            area.riskGain || NULL_VALUE
                          )}
                        </td>
                        {canEditCards && (
                          <td>
                            {editing ? (
                              <div className="d-flex gap-2">
                                <StylishNewButton
                                  className="button button--link"
                                  onClick={() => saveEditing(area)}
                                >
                                  <SharedIcon iconName="check" color="light" />
                                </StylishNewButton>
                                <StylishNewButton
                                  className="button button--link"
                                  onClick={cancelEditing}
                                >
                                  <SharedIcon iconName="close" color="light" />
                                </StylishNewButton>
                              </div>
                            ) : (
                              <div className="d-flex gap-2">
                                <StylishNewButton
                                  className="button button--link"
                                  onClick={() => startEditing(area)}
                                >
                                  <SharedIcon iconName="edit" color="light" />
                                </StylishNewButton>
                                <StylishNewButton
                                  className="button button--link"
                                  onClick={() => handleDelete(area)}
                                >
                                  <SharedIcon iconName="delete" color="light" />
                                </StylishNewButton>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="section-main d-flex flex-column align-items-center">
              <div className="d-flex justify-content-center">
                <SharedIcon iconName="layers" classes="me-2" size="lg" />
              </div>
              <div className="fs-5 mb-2"> No incident areas defined</div>
              <div className="mb-2">
                Add incident areas to track hazards, risks, and mitigation
                strategies
                <br />
                Each area should include identified hazards/risks, corresponding
                mitigations, and a Risk / Gain
              </div>
            </div>
          )}
        </div>
      </ConditionalCollapsibleCard>
      {canEditCards && (
        <IncidentAreaModal
          show={show}
          setShow={setShow}
          incidentId={incidentId}
        />
      )}
    </>
  );
};

export default IAPSafetyAnalysis;
